<template>
  <!-- SUMMARY -->
  <div
    v-if="isSupplyOrganization || isCuratorOrganization"
    class="pane-header">
    <span class="icon"><icon-colored-summary /></span>
    <span class="text">{{ capitalizeTranslation('names.summary') }}</span>
    <div class="actions">
      <div class="inline">
        <div class="input-container">
          <div class="icon">
            <icon-calendar />
          </div>
          <div>
            <multiselect
              v-model="dateRange"
              track-by="id"
              label="name"
              :allow-empty="false"
              :show-labels="false"
              :placeholder="capitalizeTranslation('messages.selectDates')"
              :options="datePickerOptions.ranges"
              @open="onMultiSelectOpen" />
          </div>
        </div>
        <template v-if="dateRange != null && dateRange.id === 'custom'">
          <date-range-picker
            style="margin-left: 1rem"
            :start="customStartDate"
            :end="customEndDate"
            :options="{ opens: 'left', ranges: null }"
            @date-range-changed="customDateRangeChanged" />
        </template>
        <button
          v-if="dateRange != null"
          class="btn btn-xs btn-default"
          @click="handleDateRangeCleared">
          {{ $t('actions.clear') }}
        </button>
      </div>
    </div>
  </div>
  <div
    v-if="isSupplyOrganization || isCuratorOrganization"
    class="row">
    <div class="col-12">
      <div class="pane-content">
        <div class="kpis">
          <!-- REVENUE -->
          <kpi
            :label="
              capitalizeTranslation(isCuratorOrganization && deal.isCuratedDeal ? 'names.spend' : 'names.revenue')
            "
            :value="revenueFormatted"
            :is-loading="isLoadingStats"
            icon="icon-colored-ad-spend"
            :is-money="true"
            :big-icon="true" />

          <!-- IMPRESSIONS DELIVERED -->
          <kpi
            :label="$t('names.impressionDelivered', { n: 2 })"
            :value="impressionsDeliveredFormatted"
            :is-loading="isLoadingStats"
            icon="icon-colored-impressions-delivered"
            :big-icon="true" />

          <!-- AVG CPM -->
          <kpi
            :label="$t('messages.averagePaidCPM')"
            :value="averageCPMFormatted"
            :is-loading="isLoadingStats"
            :is-money="true"
            icon="icon-colored-cpm"
            :big-icon="true" />
        </div>
      </div>
    </div>
  </div>

  <!-- RTB Metrics -->
  <r-t-b-metrics
    v-if="!props.isExchange"
    :entity="deal"
    :filter-start-date="filterStartDate"
    :filter-end-date="filterEndDate" />

  <div
    v-if="isSupplyOrganization || isCuratorOrganization"
    class="row">
    <!-- REVENUE CHART -->
    <div class="col-xl-6">
      <single-chart
        :template-ref="'dealRevenueChart'"
        :chart-name="
          capitalizeTranslation(isCuratorOrganization && deal.isCuratedDeal ? 'names.spend' : 'names.revenue')
        "
        :chart-theme="ChartTheme.ONE_TONE_BLUE"
        :chart-options="hourlyRevenueChartOptions"
        :chart-actions="hourlyRevenueChartActions"
        :is-loading="isLoadingGraphDetailsStats"
        :show-chart="!isLoadingGraphDetailsStats && !showNoResultsFound">
        <template #icon>
          <icon-colored-revenue />
        </template>
      </single-chart>
    </div>

    <div
      v-if="isCuratorOrganization && deal.isCuratedDeal"
      class="col-xl-6">
      <single-chart
        :template-ref="'curatedDealMarkupChart'"
        :chart-name="capitalizeTranslation('names.markup')"
        :chart-theme="ChartTheme.ONE_TONE_BLUE"
        :chart-options="hourlyCuratedMarkupChartOptions"
        :chart-actions="hourlyCuratedMarkupChartActions"
        :is-loading="isLoadingGraphDetailsStats"
        :show-chart="!isLoadingGraphDetailsStats && !showNoResultsFound">
        <template #icon>
          <icon-colored-markup style="stroke: none" />
        </template>
      </single-chart>
    </div>

    <!-- IMPRESSIONS CHART -->
    <div class="col-xl-6">
      <single-chart
        :template-ref="'dealImpressionsChart'"
        :chart-name="capitalize($t('names.impression', { n: 2 }))"
        :chart-theme="ChartTheme.ONE_TONE_GREEN"
        :chart-options="hourlyImpressionsChartOptions"
        :chart-actions="hourlyImpressionsChartActions"
        :is-loading="isLoadingGraphDetailsStats"
        :show-chart="!isLoadingGraphDetailsStats && !showNoResultsFound">
        <template #icon>
          <icon-colored-impressions-delivered />
        </template>
      </single-chart>
    </div>

    <!-- AVG CPM CHART -->
    <div
      v-if="!hideAvgCPMChart"
      class="col-xl-6">
      <single-chart
        :template-ref="'dealAvgCPMChart'"
        :chart-name="capitalizeTranslation('messages.averagePaidCPM')"
        :chart-theme="ChartTheme.ONE_TONE_YELLOW"
        :chart-options="hourlyAvgCPMChartOptions"
        :chart-actions="hourlyAvgCPMChartActions"
        :is-loading="isLoadingGraphDetailsStats"
        :show-chart="!isLoadingGraphDetailsStats && !showNoResultsFound">
        <template #icon>
          <icon-colored-c-p-m />
        </template>
      </single-chart>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { DateTime } from 'luxon';
  import { capitalize, computed, onMounted, Ref, ref, watch } from 'vue';

  import ChartDownloadGroupActionsUtils from '@/common/components/chart/chart-download-group-actions';
  import ChartFullscreenActions from '@/common/components/chart/chart-fullscreen-actions';
  import ChartTimezoneGroupActionsUtils from '@/common/components/chart/chart-timezone-group-actions';
  import ChartTrendActionsUtils from '@/common/components/chart/chart-trend-actions';
  import ChartTypeGroupActions from '@/common/components/chart/chart-type-group-actions';
  import DateRangePicker from '@/common/components/controls/DateRangePicker.vue';
  import SingleChart from '@/common/components/controls/SingleChart.vue';
  import RTBMetrics from '@/common/components/RTBMetrics.vue';
  import CellType from '@/common/enums/cell-type.enum';
  import ChartTheme from '@/common/enums/chart-theme.enum';
  import ChartType from '@/common/enums/chart-type.enum';
  import DateRangePickerEvents from '@/common/enums/datepicker-events.enum';
  import {
    IconColoredCPM,
    IconColoredImpressionsDelivered,
    IconColoredMarkup,
    IconColoredRevenue,
    IconColoredSummary,
  } from '@/common/icons/colored';
  import { IconCalendar } from '@/common/icons/monochrome';
  import { Package } from '@/common/models';
  import ChartGroupAction from '@/common/models/chart/ChartGroupAction';
  import TimeseriesAxisX from '@/common/models/chart/TimeseriesAxisX';
  import { AuctionType } from '@/common/models/package/AuctionType.enum';
  import GlobalEventBus from '@/GlobalEventBus';
  import { capitalizeTranslation, DASH, formatCurrency, serverDateFormat } from '@/globals';
  import Kpi from '@/org-app/components/controls/KPI.vue';
  import formatNumber from '@/utils/NumberFormatter';
  import { mapGetters, mapOrgAppActions } from '@/utils/StateMapping';
  import i18n from '@/vueI18n';

  interface IDealDetailsSummaryProps {
    isSupplyOrganization: boolean;
    isCuratorOrganization: boolean;
    isExchange: boolean;
    deal: Package;
    isNGExternalOrPreferredExternalDeal: boolean;
  }

  const props = defineProps<IDealDetailsSummaryProps>();

  const { getPackageSummaryStats, getPackageDetails, getCuratedPackageDetails, setPackageSummaryDateRange } =
    mapOrgAppActions();
  const { shouldBlurMoneyAmounts } = mapGetters();

  const dateRange = ref(null);
  const customStartDate = ref(DateTime.local().startOf('day').minus({ days: 7 }));
  const customEndDate = ref(DateTime.local().startOf('day'));
  const impressionsDelivered: Ref<number> = ref(null);
  const isLoadingStats = ref(false);
  const isLoadingGraphDetailsStats = ref(false);
  const averageCPM = ref(0);
  const revenue = ref(0);
  const hourlyImpressionsChartOptions = ref({});
  const hourlyImpressionsChartActions: Ref<ChartGroupAction[]> = ref([]);
  const hourlyRevenueChartOptions = ref({});
  const hourlyRevenueChartActions: Ref<ChartGroupAction[]> = ref([]);
  const hourlyAvgCPMChartOptions = ref({});
  const hourlyAvgCPMChartActions: Ref<ChartGroupAction[]> = ref([]);
  const hourlyCuratedMarkupChartOptions = ref({});
  const hourlyCuratedMarkupChartActions: Ref<ChartGroupAction[]> = ref([]);

  const datePickerOptions = computed(() => {
    const options = {
      ranges: [],
    };
    options.ranges.push({
      id: 'today',
      name: capitalizeTranslation('options.dateRangePicker.dateRange.today'),
      range: [DateTime.local().startOf('day'), DateTime.local().endOf('day')],
    });
    options.ranges.push({
      id: 'yesterday',
      name: capitalizeTranslation('options.dateRangePicker.dateRange.yesterday'),
      range: [DateTime.local().startOf('day').minus({ days: 1 }), DateTime.local().endOf('day').minus({ days: 1 })],
    });
    options.ranges.push({
      id: 'last7Days',
      name: capitalizeTranslation('options.dateRangePicker.dateRange.last7Days'),
      range: [DateTime.local().startOf('day').minus({ days: 6 }), DateTime.local().endOf('day')],
    });
    options.ranges.push({
      id: 'last30Days',
      name: capitalizeTranslation('options.dateRangePicker.dateRange.last30Days'),
      range: [DateTime.local().startOf('day').minus({ days: 29 }), DateTime.local().endOf('day')],
    });
    options.ranges.push({
      id: 'thisWeek',
      name: capitalizeTranslation('options.dateRangePicker.dateRange.thisWeek'),
      range: [
        DateTime.local()
          .startOf('year')
          .plus({ months: (DateTime.local().quarter - 1) * 3 }),
        DateTime.local().endOf('day'),
      ],
    });
    options.ranges.push({
      id: 'lastMonth',
      name: capitalizeTranslation('options.dateRangePicker.dateRange.lastMonth'),
      range: [
        DateTime.local().minus({ months: 1 }).startOf('month'),
        DateTime.local().minus({ months: 1 }).endOf('month'),
      ],
    });
    options.ranges.push({
      id: 'custom',
      name: capitalizeTranslation('messages.custom'),
      range: [customStartDate.value, customEndDate.value],
    });
    return options;
  });

  const hideAvgCPMChart = computed(
    () =>
      props.deal?.isPGDeal ||
      (props.isNGExternalOrPreferredExternalDeal && props.deal?.auctionType === AuctionType.FixedPrice),
  );

  const filterStartDate = computed(() => {
    if (dateRange.value == null) {
      return null;
    }
    return dateRange.value.id === 'custom' ? customStartDate.value : dateRange.value.range[0];
  });

  const filterEndDate = computed(() => {
    if (dateRange.value == null) {
      return null;
    }
    return dateRange.value.id === 'custom' ? customEndDate.value : dateRange.value.range[1];
  });

  const showNoResultsFound = computed(() => !impressionsDelivered.value && !revenue.value);

  const impressionsDeliveredFormatted = computed(() => {
    if (impressionsDelivered.value != null && impressionsDelivered.value > 0) {
      return formatNumber(impressionsDelivered.value, '0,0');
    }
    return formatNumber(0, '0,0');
  });

  const revenueFormatted = computed(() => {
    return getFormattedMonetaryValue(revenue.value);
  });
  const averageCPMFormatted = computed(() => {
    return getFormattedMonetaryValue(averageCPM.value);
  });

  function getFormattedMonetaryValue(value: number) {
    if (value == null || value === 0) {
      return DASH;
    }
    if (props.isCuratorOrganization) {
      return formatCurrency(value, { currencyCode: props.deal.currencyInformation.currency, showCurrencyCode: true });
    }
    return i18n.global.n(value, 'currency');
  }

  function onMultiSelectOpen() {
    if (dateRange.value?.id === 'custom') {
      GlobalEventBus.$emit(DateRangePickerEvents.CLOSE);
    }
  }

  async function refreshSummaryStats(data) {
    isLoadingStats.value = true;
    try {
      const summaryStats = await getPackageSummaryStats(data);
      averageCPM.value = summaryStats.cpm;
      impressionsDelivered.value = summaryStats.impressions;
      revenue.value = summaryStats.spend;
    } catch (e) {
      // Do nothing
      return;
    } finally {
      isLoadingStats.value = false;
    }
  }

  async function refreshDetailsGraph(data) {
    let detailStats;
    let curatedDetailStats;
    isLoadingGraphDetailsStats.value = true;
    const { start } = data;
    try {
      detailStats = await getPackageDetails(data);
      if (props.isCuratorOrganization && props.deal.isCuratedDeal) {
        curatedDetailStats = await getCuratedPackageDetails(data);
      }
    } catch (e) {
      // Do nothing
      return;
    } finally {
      isLoadingGraphDetailsStats.value = false;
    }

    const { resolution, buckets } = detailStats;
    const impressionStats = [];
    const revenueStats = [];
    const avgCpmStats = [];

    buckets.forEach((bucket) => {
      const { stats } = bucket;
      impressionStats.push(stats.impressions);
      revenueStats.push(stats.spend);
      avgCpmStats.push(stats.cpm);
    });

    const curatedMarkupStats = curatedDetailStats ? curatedDetailStats.buckets.map(({ revenue }) => revenue) : [];

    const impressionSeriesFormatter = (value) => i18n.global.n(parseInt(value));
    const currencySeriesFormatter = (value) =>
      formatCurrency(value, { currencyCode: props.deal.currencyInformation.currency ?? null, showCurrencyCode: true });
    let timeUnit = 'hours';
    let timeFormat: Intl.DateTimeFormatOptions | string = DateTime.DATETIME_MED;
    switch (resolution) {
      case 'DAILY':
        timeUnit = 'days';
        timeFormat = DateTime.DATE_SHORT;
        break;
      case 'WEEKLY':
        timeUnit = 'weeks';
        timeFormat = DateTime.DATE_SHORT;
        break;
      case 'MONTHLY':
        timeUnit = 'months';
        timeFormat = 'LLL yyyy';
        break;
      case 'YEARLY':
      case 'LIFETIME':
        timeUnit = 'years';
        timeFormat = 'yyyy';
        break;
      default:
        break;
    }

    const xAxisLabel = capitalizeTranslation('names.date');
    const { userTz } = ChartTimezoneGroupActionsUtils;
    const averageCPMLabel = capitalizeTranslation('names.averageCPM');
    const impressionsLabel = capitalize(i18n.global.t('names.impression', { n: 2 }));
    const revenueLabel = capitalizeTranslation(
      props.isCuratorOrganization && props.deal.isCuratedDeal ? 'names.spend' : 'names.revenue',
    );
    const markupLabel = capitalizeTranslation('names.markup');
    const xAxisTimeFormatter = (value, zone) => {
      const date = ChartTimezoneGroupActionsUtils.localizedDate(value, zone);

      if (typeof timeFormat === 'string') {
        return date.toFormat(timeFormat);
      }

      return date.toLocaleString(timeFormat);
    };
    const tooltipFormatter = (params, dataFormatter) => {
      const param = params[0];
      let series = `${param.axisValueLabel}<br />`;
      if (param.value != null) {
        const { marker, seriesName } = param;
        const value = dataFormatter(param.value);
        series += `${marker} ${seriesName}: ${value}<br />`;
      }
      return series;
    };

    const impressionsDuration = impressionStats.slice(0).map((x, i) => {
      const plus = {};
      plus[timeUnit] = i + 0;

      const date = DateTime.fromISO(start).plus(plus);

      return date.toMillis();
    });

    const revenueDuration = revenueStats.slice(0).map((x, i) => {
      const plus = {};
      plus[timeUnit] = i + 0;

      const date = DateTime.fromISO(start).plus(plus);

      return date.toMillis();
    });

    const curatedMarkupDuration = curatedMarkupStats.slice(0).map((x, i) => {
      const plus = {};
      plus[timeUnit] = i + 0;

      const date = DateTime.fromISO(start).plus(plus);

      return date.toMillis();
    });

    const cpmDuration = avgCpmStats.slice(0).map((x, i) => {
      const plus = {};
      plus[timeUnit] = i + 0;

      const date = DateTime.fromISO(start).plus(plus);

      return date.toMillis();
    });

    hourlyImpressionsChartOptions.value = {
      grid: {
        left: 60,
        right: 20,
        bottom: 45,
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params) => tooltipFormatter(params, (value) => impressionSeriesFormatter(value)),
      },
      calculable: true,
      xAxis: TimeseriesAxisX.fromObject({
        cellType: CellType.DATE_TIME,
        name: xAxisLabel,
        data: impressionsDuration.map((h) => xAxisTimeFormatter(h, userTz)),
        raw: impressionsDuration,
        transform: xAxisTimeFormatter,
        tz: userTz,
        granularity: timeUnit,
        nameTextStyle: { color: 'transparent', borderColor: 'transparent' },
      }),
      yAxis: [
        {
          axisLabel: {
            show: true,
            formatter: (params) =>
              formatNumber(params, '0a').length === 2 ? formatNumber(params, '0.0a') : formatNumber(params, '0a'),
          },
        },
      ],
      series: [
        {
          cellType: CellType.INTEGER,
          name: impressionsLabel,
          type: ChartType.BAR,
          data: impressionStats,
          transform: (value) => impressionSeriesFormatter(value),
          smooth: true,
          showAllSymbol: true,
        },
      ],
    };
    hourlyImpressionsChartActions.value = [
      ChartTypeGroupActions.getActions(0, true, [ChartType.LINE, ChartType.BAR], ChartType.BAR),
      ChartTrendActionsUtils.DefaultChartTrendActions,
      ChartTimezoneGroupActionsUtils.DefaultChartTimezoneGroupActions,
      ChartDownloadGroupActionsUtils.DefaultChartDownloadOptions,
      ChartFullscreenActions.getActions(1, false),
    ];

    hourlyRevenueChartActions.value = [
      ChartTypeGroupActions.getActions(0, true, [ChartType.LINE, ChartType.BAR], ChartType.LINE),
      ChartTrendActionsUtils.DefaultChartTrendActions,
      ChartTimezoneGroupActionsUtils.DefaultChartTimezoneGroupActions,
      ChartDownloadGroupActionsUtils.DefaultChartDownloadOptions,
      ChartFullscreenActions.getActions(1, false),
    ];
    hourlyRevenueChartOptions.value = {
      grid: {
        left: 60,
        right: 20,
        bottom: 45,
      },
      calculable: true,
      xAxis: TimeseriesAxisX.fromObject({
        cellType: CellType.DATE_TIME,
        name: xAxisLabel,
        data: revenueDuration.map((h) => xAxisTimeFormatter(h, userTz)),
        raw: revenueDuration,
        transform: xAxisTimeFormatter,
        tz: userTz,
        granularity: timeUnit,
        nameTextStyle: { color: 'transparent', borderColor: 'transparent' },
      }),
      yAxis: [
        {
          axisLabel: {
            show: !shouldBlurMoneyAmounts.value,
            formatter: formatCurrencyInCharts,
          },
        },
      ],
      series: [
        {
          cellType: CellType.CURRENCY,
          name: revenueLabel,
          type: ChartType.LINE,
          data: revenueStats,
          areaStyle: { opacity: 0.1 },
          itemStyle: { areaStyle: { type: 'default' } },
          transform: (value) => currencySeriesFormatter(value),
        },
      ],
      tooltip: {
        show: !shouldBlurMoneyAmounts.value,
        trigger: 'axis',
        formatter: (params) => tooltipFormatter(params, (value) => currencySeriesFormatter(value)),
      },
    };

    hourlyCuratedMarkupChartActions.value = [
      ChartTypeGroupActions.getActions(0, true, [ChartType.LINE, ChartType.BAR], ChartType.BAR),
      ChartTrendActionsUtils.DefaultChartTrendActions,
      ChartTimezoneGroupActionsUtils.DefaultChartTimezoneGroupActions,
      ChartDownloadGroupActionsUtils.DefaultChartDownloadOptions,
      ChartFullscreenActions.getActions(1, false),
    ];
    hourlyCuratedMarkupChartOptions.value = {
      grid: {
        left: 60,
        right: 20,
        bottom: 45,
      },
      calculable: true,
      xAxis: TimeseriesAxisX.fromObject({
        cellType: CellType.DATE_TIME,
        name: xAxisLabel,
        data: curatedMarkupDuration.map((h) => xAxisTimeFormatter(h, userTz)),
        raw: curatedMarkupDuration,
        transform: xAxisTimeFormatter,
        tz: userTz,
        granularity: timeUnit,
        nameTextStyle: { color: 'transparent', borderColor: 'transparent' },
      }),
      yAxis: [
        {
          axisLabel: {
            show: !shouldBlurMoneyAmounts.value,
            formatter: formatCurrencyInCharts,
          },
        },
      ],
      series: [
        {
          cellType: CellType.CURRENCY,
          name: markupLabel,
          type: ChartType.BAR,
          data: curatedMarkupStats,
          areaStyle: { opacity: 0.1 },
          itemStyle: { areaStyle: { type: 'default' } },
          transform: (value) => currencySeriesFormatter(value),
        },
      ],
      tooltip: {
        show: !shouldBlurMoneyAmounts.value,
        trigger: 'axis',
        formatter: (params) => tooltipFormatter(params, (value) => currencySeriesFormatter(value)),
      },
    };

    hourlyAvgCPMChartActions.value = [
      ChartTypeGroupActions.getActions(0, true, [ChartType.LINE, ChartType.BAR], ChartType.LINE),
      ChartTrendActionsUtils.DefaultChartTrendActions,
      ChartTimezoneGroupActionsUtils.DefaultChartTimezoneGroupActions,
      ChartDownloadGroupActionsUtils.DefaultChartDownloadOptions,
      ChartFullscreenActions.getActions(1, false),
    ];
    hourlyAvgCPMChartOptions.value = {
      calculable: true,
      grid: {
        left: 80,
        right: 20,
        bottom: 45,
      },
      xAxis: TimeseriesAxisX.fromObject({
        cellType: CellType.DATE_TIME,
        name: xAxisLabel,
        data: cpmDuration.map((h) => xAxisTimeFormatter(h, userTz)),
        raw: cpmDuration,
        transform: xAxisTimeFormatter,
        tz: userTz,
        granularity: timeUnit,
        nameTextStyle: { color: 'transparent', borderColor: 'transparent' },
      }),
      yAxis: [
        {
          axisLabel: {
            show: !shouldBlurMoneyAmounts.value,
            formatter: formatCurrencyInCharts,
          },
        },
      ],
      series: [
        {
          cellType: CellType.CURRENCY,
          name: averageCPMLabel,
          type: ChartType.LINE,
          data: avgCpmStats,
          smooth: true,
          areaStyle: { opacity: 0.1 },
          itemStyle: { areaStyle: { type: 'default' } },
          transform: (value) => currencySeriesFormatter(value),
        },
      ],
      tooltip: {
        show: !shouldBlurMoneyAmounts.value,
        trigger: 'axis',
        formatter: (params) => tooltipFormatter(params, (value) => currencySeriesFormatter(value)),
      },
    };
  }

  async function refreshStats(start = null, end = null) {
    if (start == null || end == null) {
      return;
    }

    const startDate = (start instanceof DateTime ? start : DateTime.fromISO(start))?.toFormat(serverDateFormat);
    const endDate = (
      end instanceof DateTime
        ? end.plus({ days: 1 }).startOf('day')
        : DateTime.fromISO(end).plus({ days: 1 }).startOf('day')
    )?.toFormat(serverDateFormat);

    if (endDate <= startDate) {
      return;
    }

    // Set the date range in the store
    setPackageSummaryDateRange({ start: startDate, end: endDate });

    const data = {
      id: props.deal.id,
      start: startDate,
      end: endDate,
    };

    refreshSummaryStats(data);
    refreshDetailsGraph(data);
  }

  function customDateRangeChanged(range) {
    if (range.start == null && range.end == null) return;
    [customStartDate.value, customEndDate.value] = [range.start, range.end];
    refreshStats(customStartDate.value, customEndDate.value);
  }

  function handleDateRangeCleared() {
    dateRange.value = null;
    refreshStats(props.deal.startDate, props.deal.endDate);
  }

  function formatCurrencyInCharts(params) {
    const { currency, symbol } = props.deal.currencyInformation;
    let formattedCurrency = formatNumber(params, props.isCuratorOrganization ? '0a' : '$0a');
    if (params > 0 && params < 100) {
      formattedCurrency = formatNumber(params, props.isCuratorOrganization ? '0.00a' : '$0.00a');
    } else if (formatNumber(params, '0a').length === 3) {
      formattedCurrency = formatNumber(params, props.isCuratorOrganization ? '0.0a' : '$0.0a');
    }
    if (currency != null && symbol != null) {
      return symbol === '$' ? `${currency}${symbol}${formattedCurrency}` : `${symbol}${formattedCurrency}`;
    }
    return formattedCurrency;
  }

  watch(
    () => dateRange.value,
    () => {
      refreshStats(filterStartDate.value, filterEndDate.value);
    },
  );

  onMounted(() => {
    if (props.isSupplyOrganization || props.isCuratorOrganization) {
      // Only get stats after a deal has started.
      if (props.deal.startDate < DateTime.local()) {
        let { endDate } = props.deal;
        if (endDate > DateTime.local()) {
          endDate = DateTime.local();
        }
        refreshStats(props.deal.startDate, endDate);
      }
    }
  });
</script>
